import { Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Store, Pizza, ListOrdered, BadgePercent, Map, Palette, Bike, UserCog, ListTree, IndianRupee, Receipt, CreditCard, PackageCheck, Image as ImageIcon, FileText, Grid3x3, LayoutGrid, Sparkles, Coins, Share2, Wallet, Clock, BadgeIndianRupee, Network, CalendarClock, Upload, Users, GitBranch, Headphones, ArrowUpRight, Package, Truck, Megaphone, ClipboardList, Undo2, BellRing, ShieldCheck, MapPinned } from "lucide-react";
import TenantStatusBanner from "../../components/TenantStatusBanner";
import { useAuth } from "../../contexts/AuthContext";
import { useTenant } from "../../contexts/TenantContext";
import { useEffect } from "react";
import AdminShell from "../../components/AdminShell";
import PersistentAlertSetting from "../../components/PersistentAlertSetting";
import NotificationGate from "../../components/NotificationGate";
import OrderAlertFeed from "../../components/OrderAlertFeed";
import { lseOn } from "../../lib/lse";

export default function AdminLayout() {
    const { user, loading, logout } = useAuth();
    const { tenant } = useTenant();
    const nav = useNavigate();
    const isAuthorized = !!user && ["TENANT_ADMIN", "SUPER_ADMIN"].includes(user.role);

    useEffect(() => {
        if (!loading && !isAuthorized) {
            nav("/login", { state: { role: "TENANT_ADMIN" } });
        }
    }, [loading, isAuthorized, nav]);

    const items = [
        { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
        { to: "/admin/restaurants", icon: Store, label: "Shops" },
        { to: "/admin/categories", icon: ListTree, label: "Categories" },
        { to: "/admin/products", icon: Pizza, label: "Products" },
        { to: "/admin/inventory", icon: PackageCheck, label: "Inventory" },
        { to: "/admin/pricing", icon: IndianRupee, label: "Pricing" },
        { to: "/admin/payments", icon: CreditCard, label: "Payments" },
        { to: "/admin/payment-records", icon: Undo2, label: "Payment Refunds" },
        { to: "/admin/charges", icon: Receipt, label: "Charges" },
        { to: "/admin/orders", icon: ListOrdered, label: "Orders" },
        { to: "/admin/workflow", icon: GitBranch, label: "Order Workflow" },
        { to: "/admin/bookings", icon: CalendarClock, label: "Bookings" },
        { to: "/admin/coupons", icon: BadgePercent, label: "Coupons" },
        { to: "/admin/zones", icon: Map, label: "Zones" },
        { to: "/admin/riders", icon: Bike, label: "Riders" },
        { to: "/admin/rider-ledger", icon: BadgeIndianRupee, label: "Rider Ledger" },
        { to: "/admin/settlements", icon: Wallet, label: "Shop Settlements" },
        { to: "/admin/booking-settlements", icon: CalendarClock, label: "Booking Settlements" },
        { to: "/admin/commission-invoices", icon: FileText, label: "Commission Invoices" },
        { to: "/admin/zone-admins", icon: Network, label: "Zone Admins" },
        { to: "/admin/owners", icon: UserCog, label: "Owners" },
        { to: "/admin/team", icon: ShieldCheck, label: "Team" },
        { to: "/admin/customers", icon: Users, label: "Customers" },
        { to: "/admin/withdrawals", icon: ArrowUpRight, label: "Withdrawals" },
        { to: "/admin/hours", icon: Clock, label: "Hours & Setup" },
        { to: "/admin/imports", icon: Upload, label: "Bulk Import" },
        { to: "/admin/branding", icon: Palette, label: "Branding" },
        { to: "/admin/support-contact", icon: Headphones, label: "Support Contact" },
        { to: "/admin/bulk-notifications", icon: Megaphone, label: "Bulk Notifications" },
        { to: "/admin/notifications", icon: BellRing, label: "Notifications" },
        { to: "/admin/policies", icon: FileText, label: "Legal Policies" },
        { to: "/admin/banners", icon: ImageIcon, label: "Banners" },
        { to: "/admin/business-categories", icon: Grid3x3, label: "Verticals" },
        { to: "/admin/category-groups", icon: Package, label: "Group Categories" },
        { to: "/admin/hero-tiles", icon: LayoutGrid, label: "Home Tiles" },
        { to: "/admin/home-sections", icon: ListTree, label: "Home Sections" },
        { to: "/admin/theme", icon: Sparkles, label: "Theme" },
        { to: "/admin/coins", icon: Coins, label: "Coins" },
        { to: "/admin/referral", icon: Share2, label: "Referral" },
        { to: "/admin/shop-submissions", icon: ClipboardList, label: "Shop Submissions" },
        { to: "/admin/billing", icon: Wallet, label: "Billing" },
        { to: "/admin/reports", icon: FileText, label: "Reports" },
        // iter124 — Shiprocket nav item only visible when Super Admin
        // has enabled the integration for this tenant. Hidden entirely
        // otherwise so admins never see "empty" or "disabled" nav rows.
        ...(tenant?.shiprocket_enabled ? [{ to: "/admin/shiprocket", icon: Truck, label: "Shiprocket" }] : []),
        // Same pattern — Order Returns nav item only when Super Admin
        // has enabled it for this tenant.
        ...(tenant?.returns_enabled ? [{ to: "/admin/returns", icon: Undo2, label: "Returns" }] : []),
        // Location Service Engine hub — only when the Super Admin switched it on for this tenant.
        ...(lseOn(tenant) ? [{ to: "/admin/lse", icon: MapPinned, label: "Location Service" }] : []),
    ];

    if (!isAuthorized) return null;
    return (
        <AdminShell
            items={items}
            user={user}
            title="Tenant Admin"
            testidPrefix="admin-nav"
            sidebarExtra={<PersistentAlertSetting />}
            onLogout={async () => { try { await logout(); } finally { nav("/login"); } }}
        >
            <TenantStatusBanner />
            <OrderAlertFeed />
            <NotificationGate />
            <Outlet />
        </AdminShell>
    );
}
